import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * AGC: Hook that centralises swipe logic for carousel-style selectors.
 */
interface UseSwipeCarouselOptions {
  /** AGC: Total number of items in the carousel. */
  length: number
  /** AGC: Index to initialise the carousel with. */
  initialIndex?: number
  /** AGC: Controlled index supplied by parent to force alignment. */
  controlledIndex?: number
  /** AGC: Whether the carousel should wrap around when reaching bounds. */
  loop?: boolean
  /** AGC: Callback fired whenever the index changes due to swipe or navigation. */
  onIndexChange?: (index: number) => void
}

/**
 * AGC: Internal reference that tracks drag metadata.
 */
interface DragState {
  /** AGC: Horizontal screen coordinate where the drag started. */
  startX: number
  /** AGC: Latest measured horizontal position during the drag. */
  lastX: number
  /** AGC: Indicates whether the pointer is currently dragging. */
  isDragging: boolean
  /** AGC: Accumulated delta in pixels for the drag gesture. */
  delta: number
  /** AGC: Flag used to distinguish mouse drags from clicks. */
  isMouseDown: boolean
}

/**
 * AGC: Reusable swipe hook that abstracts pointer handling for carousels.
 */
export const useSwipeCarousel = ({
  length,
  initialIndex = 0,
  controlledIndex,
  loop = true,
  onIndexChange
}: UseSwipeCarouselOptions) => {
  /** AGC: Threshold in pixels before we treat a drag as a navigation intent. */
  const SWIPE_THRESHOLD = 45

  /** AGC: Current index managed internally by the hook (normalised). */
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (length === 0) {
      return 0
    }
    return Math.min(Math.max(initialIndex, 0), length - 1)
  })
  /** AGC: Virtual index preserving swipe direction continuity when looping. */
  const [virtualIndex, setVirtualIndex] = useState(() => {
    if (length === 0) {
      return 0
    }
    return initialIndex
  })
  const virtualIndexRef = useRef(virtualIndex)

  /** AGC: Tracks whether the carousel is actively being dragged. */
  const [isDragging, setIsDragging] = useState(false)
  /** AGC: Stores the temporary pixel offset applied during drag. */
  const [dragOffset, setDragOffset] = useState(0)

  /** AGC: Mutable drag metadata that does not trigger re-renders. */
  const dragStateRef = useRef<DragState>({
    startX: 0,
    lastX: 0,
    isDragging: false,
    delta: 0,
    isMouseDown: false
  })

  /** AGC: Tracks when the index change originated from an external sync. */
  const suppressCallbackRef = useRef(false)
  /** AGC: Signals whether the next commit should trigger the external callback. */
  const pendingCallbackRef = useRef(true)

  /**
   * AGC: Helper that normalises the target index respecting bounds and looping.
   */
  const deriveDisplayIndex = useCallback(
    (index: number) => {
      if (length === 0) {
        return 0
      }

      if (loop) {
        const wrapped = (index % length + length) % length
        return wrapped
      }

      return Math.min(Math.max(index, 0), length - 1)
    },
    [length, loop]
  )

  /**
   * AGC: Clamp helper used when looping is disabled.
   */
  const clampIndex = useCallback(
    (index: number) => {
      if (length === 0) {
        return 0
      }
      return Math.min(Math.max(index, 0), length - 1)
    },
    [length]
  )

  /**
   * AGC: Central index setter that avoids redundant updates and lets effects fire callbacks.
   */
  const commitIndex = useCallback(
    (index: number) => {
      pendingCallbackRef.current = true
      if (loop) {
        setVirtualIndex(index)
        setCurrentIndex((previous) => {
          const next = deriveDisplayIndex(index)
          if (previous === next) {
            return previous
          }
          return next
        })
      } else {
        const clamped = clampIndex(index)
        setVirtualIndex(clamped)
        setCurrentIndex((previous) => {
          if (previous === clamped) {
            return previous
          }
          return clamped
        })
      }
    },
    [clampIndex, deriveDisplayIndex, loop]
  )

  /** AGC: Setter exposed to consumers with optional callback control. */
  const setIndex = useCallback(
    (index: number, options?: { triggerCallback?: boolean }) => {
      if (options && options.triggerCallback === false) {
        pendingCallbackRef.current = false
      } else {
        pendingCallbackRef.current = true
      }
      commitIndex(index)
    },
    [commitIndex]
  )

  /** AGC: Resets drag metadata when a gesture completes. */
  const resetDragState = useCallback(() => {
    dragStateRef.current = {
      startX: 0,
      lastX: 0,
      isDragging: false,
      delta: 0,
      isMouseDown: false
    }
    setDragOffset(0)
    setIsDragging(false)
  }, [])

  /** AGC: Starts a drag gesture using a given pointer coordinate. */
  const beginDrag = useCallback((clientX: number, isMouse: boolean) => {
    dragStateRef.current = {
      startX: clientX,
      lastX: clientX,
      isDragging: true,
      delta: 0,
      isMouseDown: isMouse
    }
    setIsDragging(true)
  }, [])

  /** AGC: Updates drag delta as the pointer moves horizontally. */
  const updateDrag = useCallback(
    (clientX: number) => {
      const state = dragStateRef.current
      if (!state.isDragging) {
        return
      }

      state.lastX = clientX
      state.delta = clientX - state.startX

      /** AGC: Apply resistance on bounds when looping is disabled. */
      const atEdge = (!loop && currentIndex <= 0 && state.delta > 0) || (!loop && currentIndex >= length - 1 && state.delta < 0)
      const effectiveDelta = atEdge ? state.delta * 0.35 : state.delta
      setDragOffset(effectiveDelta)
    },
    [currentIndex, length, loop]
  )

  /** AGC: Completes the drag gesture and navigates if the threshold is crossed. */
  const endDrag = useCallback(() => {
    const state = dragStateRef.current
    if (!state.isDragging) {
      return
    }

    const delta = state.delta
    resetDragState()

    if (Math.abs(delta) < SWIPE_THRESHOLD) {
      return
    }

    const direction = delta > 0 ? -1 : 1
    const baseIndex = loop ? virtualIndexRef.current : currentIndex
    commitIndex(baseIndex + direction)
  }, [SWIPE_THRESHOLD, commitIndex, currentIndex, loop, resetDragState])

  /** AGC: Touch start handler exposed to consuming components. */
  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (event.touches.length > 1) {
        return
      }
      beginDrag(event.touches[0].clientX, false)
    },
    [beginDrag]
  )

  /** AGC: Touch move handler that updates drag delta and prevents vertical blocking. */
  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!dragStateRef.current.isDragging || event.touches.length > 1) {
        return
      }
      updateDrag(event.touches[0].clientX)
    },
    [updateDrag]
  )

  /** AGC: Touch end handler finalising the gesture. */
  const handleTouchEnd = useCallback(() => {
    endDrag()
  }, [endDrag])

  /** AGC: Mouse down handler enabling desktop drag interactions. */
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.button !== 0) {
        return
      }
      beginDrag(event.clientX, true)
    },
    [beginDrag]
  )

  /** AGC: Mouse move handler mirroring touch behaviour. */
  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!dragStateRef.current.isMouseDown) {
        return
      }
      updateDrag(event.clientX)
    },
    [updateDrag]
  )

  /** AGC: Mouse up handler releasing the drag gesture. */
  const handleMouseUp = useCallback(() => {
    if (!dragStateRef.current.isMouseDown) {
      return
    }
    endDrag()
  }, [endDrag])

  /** AGC: Ensures drag state resets if the pointer leaves the carousel bounds. */
  const handleMouseLeave = useCallback(() => {
    if (!dragStateRef.current.isMouseDown) {
      return
    }
    endDrag()
  }, [endDrag])

  /** AGC: Keyboard navigation handler offering arrow key support. */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        const baseIndex = loop ? virtualIndexRef.current : currentIndex
        commitIndex(baseIndex + 1)
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault()
        const baseIndex = loop ? virtualIndexRef.current : currentIndex
        commitIndex(baseIndex - 1)
      }
    },
    [commitIndex, currentIndex, loop]
  )

  /** AGC: Clamp the index if the carousel length shrinks. */
  useEffect(() => {
    setCurrentIndex((previous) => {
      if (length === 0) {
        return 0
      }
      const capped = Math.min(previous, length - 1)
      return capped
    })
  }, [length])

  /** AGC: Track the latest index for controlled sync checks. */
  const previousIndexRef = useRef(currentIndex)
  const currentIndexRef = useRef(currentIndex)
  useEffect(() => {
    virtualIndexRef.current = virtualIndex
  }, [virtualIndex])

  useEffect(() => {
    currentIndexRef.current = currentIndex
  }, [currentIndex])

  /** AGC: Align internal index when a controlled value is provided by the parent. */
  useEffect(() => {
    if (controlledIndex === undefined) {
      return
    }
    const targetDisplay = deriveDisplayIndex(controlledIndex)
    if (targetDisplay === currentIndexRef.current) {
      return
    }
    suppressCallbackRef.current = true
    pendingCallbackRef.current = false
    if (loop) {
      const currentVirtual = virtualIndexRef.current
      const currentDisplay = deriveDisplayIndex(currentVirtual)
      let targetVirtual = currentVirtual + (targetDisplay - currentDisplay)
      if (length > 0) {
        // AGC: Ensure the movement preserves direction continuity
        if (targetVirtual - currentVirtual > length / 2) {
          targetVirtual -= length
        } else if (currentVirtual - targetVirtual > length / 2) {
          targetVirtual += length
        }
      }
      setVirtualIndex(targetVirtual)
      setCurrentIndex(targetDisplay)
    } else {
      const clamped = clampIndex(controlledIndex)
      setVirtualIndex(clamped)
      setCurrentIndex(clamped)
    }
    setDragOffset(0)
  }, [clampIndex, controlledIndex, deriveDisplayIndex, length, loop])

  /** AGC: Emit index change callbacks only after React commits the update. */

  useEffect(() => {
    if (previousIndexRef.current !== currentIndex) {
      if (suppressCallbackRef.current) {
        suppressCallbackRef.current = false
      } else if (pendingCallbackRef.current) {
        onIndexChange?.(currentIndex)
      }
      pendingCallbackRef.current = true
      previousIndexRef.current = currentIndex
    }
  }, [currentIndex, onIndexChange])

  return useMemo(
    () => ({
      currentIndex,
      virtualIndex,
      setIndex,
      isDragging,
      dragOffset,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleMouseLeave,
      handleKeyDown
    }),
    [
      currentIndex,
      dragOffset,
      handleKeyDown,
      handleMouseDown,
      handleMouseLeave,
      handleMouseMove,
      handleMouseUp,
      handleTouchEnd,
      handleTouchMove,
      handleTouchStart,
      isDragging,
      loop,
      setIndex,
      virtualIndex
    ]
  )
}

export type UseSwipeCarouselReturn = ReturnType<typeof useSwipeCarousel>

